import React, { useCallback, useEffect, useMemo, useState } from 'react';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { titleize, underscored } from 'underscore.string';
import { ChevronDown, Plus } from 'lucide-react';

import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import Container from 'components/v2/container';
import SessionStore from 'stores/SessionStore';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import { getAccessorials } from 'utils/AccessorialWebAPIUtils';

import { FilterCardHeader } from 'components/design-system/molecules/FilterCardHeader';
import { FilterBarRegistry } from 'components/design-system/molecules/FilterBarRegistry';
import {
  useFilterState,
  initialValuesFromConfigs,
} from 'components/design-system/hooks/useFilterState';
import { buildQuery } from 'components/design-system/utils/buildQuery';

import { AccessorialRatesTable } from './AccessorialRatesTable';
import { StandardRatesModal } from './StandardRatesModal';
import { makeAccessorialRatesFilters } from './AccessorialRates.filters';

export function AccessorialRates() {
  const { t } = useTranslation();

  const pathIncludesModalVal = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const queryCustomer = pathIncludesModalVal && pathIncludesModalVal.customer;
  const queryCustomerName =
    pathIncludesModalVal && pathIncludesModalVal.customerName;

  // --- async-loaded option data -------------------------------------------
  const [marketsList, setMarketsList] = useState([]);
  const [accessorials, setAccessorials] = useState([]);

  // --- standard rates modal state -----------------------------------------
  const [standardRateType, setStandardRateType] = useState('');
  const [showStandardRatesModal, setShowStandardRatesModal] = useState(false);

  // --- add rate modal trigger (lifted from table) -------------------------
  const [addModalOpen, setAddModalOpen] = useState(false);

  const [canUpdate] = useState(SessionStore.isAbleToEditAccessorialLaneRate());

  // --- filter state via design-system hook --------------------------------
  const FILTERS = useMemo(
    () =>
      makeAccessorialRatesFilters({
        markets: marketsList,
        accessorials,
      }),
    [marketsList, accessorials],
  );
  const initialValues = useMemo(
    () => initialValuesFromConfigs(FILTERS),
    [FILTERS],
  );
  const { values, displayed, labels, setValue, setMany, setLabel, toggle } =
    useFilterState(initialValues);

  // --- Seed customer from query string (on mount) -------------------------
  const initialCustomer = queryCustomer;
  const initialCustomerName = queryCustomerName;
  useEffect(() => {
    if (initialCustomer) {
      setValue('customer', [initialCustomer]);
      if (initialCustomerName) {
        setLabel('customer', initialCustomer, initialCustomerName);
      }
    }
  }, [initialCustomer, initialCustomerName, setValue, setLabel]);

  // --- Fetch markets + accessorials on mount ------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await MarketsAPIUtils.getMarkets();
        if (!cancelled && result.entity.markets) {
          const mapped = result.entity.markets.map((m) => ({
            _id: m.id,
            name: m.name,
            slug: m.name,
          }));
          setMarketsList(mapped);
        }
      } catch (e) {
        console.error('failed to fetch market values: ', e);
      }
    })();
    (async () => {
      try {
        const result = await getAccessorials({ paginate: false });
        if (!cancelled) {
          const items = result.entity.items.map((a) => ({
            _id: a._id,
            name: a.name,
            slug: a.slug || underscored(a.name.toLowerCase()),
          }));
          setAccessorials(items);
        }
      } catch (e) {
        console.error('failed to fetch accessorials: ', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Derive API-ready filter values for table ---------------------------
  const filterQuery = useMemo(
    () => buildQuery(FILTERS, values),
    [FILTERS, values],
  );

  // --- Filter handlers ----------------------------------------------------
  const onChangeFilter = useCallback(
    (id, value) => {
      setValue(id, value);
    },
    [setValue],
  );

  const onToggleFilter = useCallback(
    (id) => {
      toggle(id);
    },
    [toggle],
  );

  const onReset = useCallback(() => {
    const cleared = {};
    for (const cfg of FILTERS) {
      cleared[cfg.id] =
        cfg.kind === 'select-multi' || cfg.kind === 'autocomplete-multi'
          ? []
          : cfg.kind === 'toggle'
            ? false
            : null;
    }
    setMany(cleared);
  }, [FILTERS, setMany]);

  return (
    <Container
      dataTestId="Container-ced1eb72"
      className="py-6"
      useMaxWidth={false}
    >
      <Card>
        <FilterCardHeader
          title={t('ACCESSORIAL_RATES')}
          onReset={onReset}
          actions={
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    Standard Rates
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      setStandardRateType('carrier');
                      setShowStandardRatesModal(true);
                    }}
                  >
                    {t('CARRIER_STANDARD_CARD')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setStandardRateType('shipper');
                      setShowStandardRatesModal(true);
                    }}
                  >
                    {t('SHIPPER_STANDARD_CARD')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {canUpdate && (
                <Button size="sm" onClick={() => setAddModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  {titleize(t('ADD'))}
                </Button>
              )}
            </div>
          }
        >
          <FilterBarRegistry
            configs={FILTERS}
            values={values}
            labels={labels}
            displayed={displayed}
            onChange={onChangeFilter}
            onLabelChange={(id, optionId, label) =>
              setLabel(id, optionId, label)
            }
            onToggle={onToggleFilter}
          />
        </FilterCardHeader>
        <CardContent>
          <AccessorialRatesTable
            autoApply={filterQuery.autoApply ?? null}
            customer={filterQuery.customer || null}
            customerType={filterQuery.customerType || null}
            canUpdate={canUpdate}
            status={filterQuery.status || null}
            market={filterQuery.market || null}
            accessorialType={filterQuery.accessorialtype || null}
            legType={filterQuery.legType || null}
            addModalOpen={addModalOpen}
            onCloseAddModal={() => setAddModalOpen(false)}
          />
        </CardContent>
      </Card>

      <StandardRatesModal
        show={showStandardRatesModal}
        onHide={() => setShowStandardRatesModal(false)}
        canUpdate={canUpdate}
        markets={marketsList.map((m) => m.name)}
        type={standardRateType}
      />
    </Container>
  );
}
